import { twMerge } from 'tailwind-merge';

import { Badge } from '@/_components';
import {
  priorityLabels,
  type SupportPriority,
  SupportTicketPriority,
} from '@/_containers/support/shared/schemas/support-query-schema';

type PriorityBadgeProps = {
  priority: number;
  variant?: 'badge' | 'box';
  boxClassName?: string;
};

export function PriorityBadge({ priority, variant = 'badge', boxClassName }: PriorityBadgeProps) {
  const label = priorityLabels[priority as SupportPriority] || '***UNKNOWN*** Priority';

  const getBadgeColor = (priority: SupportPriority) => {
    switch (Number(priority)) {
      case SupportTicketPriority.URGENT:
        return '#FF5959'; // Urgent
      case SupportTicketPriority.HIGH:
        return '#FFD012'; // High
      case SupportTicketPriority.MEDIUM:
        return '#4DA1FF'; // Medium
      case SupportTicketPriority.LOW:
        return '#A0D76A'; // Low
      default:
        return '#A0D76A';
    }
  };

  const getBadgeType = (priority: SupportPriority) => {
    switch (Number(priority)) {
      case SupportTicketPriority.URGENT:
        return 'error';
      case SupportTicketPriority.HIGH:
        return 'warning';
      case SupportTicketPriority.MEDIUM:
        return 'info';
      case SupportTicketPriority.LOW:
        return 'success';
      default:
        return 'info';
    }
  };

  if (variant === 'box') {
    return (
      <div className="flex items-center gap-2">
        <div
          className="h-3 min-h-3 w-3 min-w-3 rounded-sm"
          style={{ backgroundColor: getBadgeColor(priority as SupportPriority) }}
        />
        <span className={twMerge('text-accent overflow-hidden text-sm text-ellipsis dark:text-gray-400', boxClassName)}>
          {label}
        </span>
      </div>
    );
  }

  return (
    <Badge
      className="overflow-hidden text-ellipsis whitespace-nowrap"
      type={getBadgeType(priority as SupportPriority)}
      style={{ backgroundColor: getBadgeColor(priority as SupportPriority) }}
    >
      {label}
    </Badge>
  );
}
